import {
  Component,
  Output,
  Input,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnInit,
  inject,
  signal,
  computed,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DxTreeViewModule, DxTreeViewComponent, DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppNavItem, AuthService, IUser, NavigationService } from '../../../services';
import { ChatNotificationsService } from '../../../shared/services/chat-notifications.service';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import { SidebarPinnedService } from '../../../shared/services/sidebar-pinned.service';
import { SidebarRecentsService } from '../../../shared/services/sidebar-recents.service';
import { ThemeService } from '../../../services/theme.service';
import { CommandPaletteService } from '../../../shared/services/command-palette.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'side-navigation-menu',
  templateUrl: './side-navigation-menu.component.html',
  styleUrls: ['./side-navigation-menu.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DxTreeViewModule,
    DxButtonModule,
    DxLoadIndicatorModule,
    TranslateModule,
    AppIconComponent,
  ],
})
export class SideNavigationMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DxTreeViewComponent, { static: true })
  menu!: DxTreeViewComponent;

  @Output()
  selectedItemChanged = new EventEmitter<DxTreeViewTypes.ItemClickEvent>();

  @Output()
  openMenu = new EventEmitter<any>();

  @Input()
  get compactMode() {
    return this._compactMode;
  }

  @Input()
  set selectedItem(value: String) {
    this._selectedItem = value;
    this.setSelectedItem();
  }

  get selectedItem(): String {
    return this._selectedItem;
  }

  @Input() width: number = 250;

  set compactMode(val) {
    this._compactMode = val;
    this.compactSig.set(val);

    if (!this.menu.instance) {
      return;
    }
    // Al entrar a compact: forzamos collapseAll() — el rail muestra SOLO los
    // iconos L1. Los L2 quedan ocultos hasta que el usuario abra el sidebar.
    // Al salir a expanded: dejamos el estado actual (DevExtreme conserva
    // expand state). Si nunca se abrio nada, todo queda colapsado igual.
    if (val) {
      this.menu.instance.collapseAll();
    }
  }

  private _selectedItem!: String;
  private _items: Record<string, unknown>[] = [];
  private _compactMode = false;

  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private auth = inject(AuthService);
  private chat = inject(ChatNotificationsService);
  private translate = inject(TranslateService);
  navigationService = inject(NavigationService);
  pinned = inject(SidebarPinnedService);
  recents = inject(SidebarRecentsService);
  themeService = inject(ThemeService);
  commandPalette = inject(CommandPaletteService);
  /** Signals derivados de BehaviorSubjects del ThemeService — para usar en
   *  template sin tener que .pipe ni async pipe. */
  isDark = toSignal(this.themeService.isDark, { initialValue: this.themeService.currentTheme === 'dark' });
  isCompact = toSignal(this.themeService.isCompact, { initialValue: this.themeService.currentDensity === 'compact' });
  private sub?: Subscription;

  searchValue = signal('');
  searchFocused = signal(false);
  compactSig = signal(false);
  user = signal<IUser | null>(null);
  scrolledTop = signal(false);
  scrolledBottom = signal(false);

  /**
   * Tooltip flotante del mini-rail. Posición `fixed` calculada del bounding
   * rect del item bajo el cursor — escapa al overflow:hidden del .nav-scroll
   * y la .menu-container, que con `position: absolute` cortaba la pildora.
   * Solo se renderiza cuando el sidebar está en compact.
   */
  hoveredText = signal<string | null>(null);
  hoveredBadge = signal<string | null>(null);
  tooltipTop = signal(0);
  tooltipLeft = signal(0);

  /**
   * Flyout submenu del mini-rail. Cuando el sidebar esta en compact y el
   * usuario hovereá un GRUPO (item con .items), mostramos un popover flotante
   * con TODOS los hijos del grupo, no solo el tooltip del label. Asi puede
   * navegar a un L2/L3 sin abrir el sidebar completo.
   * `null` cuando no hay flyout activo.
   */
  flyoutItem = signal<{ text: string; items: AppNavItem[]; top: number } | null>(null);
  /** Timeout id del cierre debounced — permite cancelar el cierre si el mouse
   *  pasa al flyout en los ~150ms posteriores al leave del item. */
  private flyoutCloseTimer: any = null;

  /** Key de sessionStorage para persistir el scrollTop del sidebar. Usamos
   *  sessionStorage (no local) porque la posicion deja de tener sentido entre
   *  sesiones — al re-loguear el set de modulos puede cambiar y un scrollTop
   *  guardado en otro contexto te apuntaria a un item distinto. */
  private readonly SCROLL_STORAGE_KEY = 'crm.sidebar.scrollTop';
  private scrollEl?: HTMLElement;
  /** Flag para no pisar la restauracion inicial con un evento de scroll
   *  emitido por el browser durante el layout. */
  private suppressScrollSave = false;

  iniciales = computed(() => {
    const u = this.user();
    const src = u?.name || u?.email || '';
    return src
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(s => s[0]?.toUpperCase() ?? '')
      .join('') || '·';
  });

  /**
   * Rol mostrado en el badge debajo del nombre. Antes se decidia por dominio
   * de email (`@ionics.com.ar` → IONICS, sino → Cliente) — gnomico, ademas
   * un usuario admin con email externo aparecia como "Cliente". Ahora se
   * deriva de los permission claims, mismo criterio que `AdminGuardService`
   * y `home.component.ts:54` (claim `cliente:viewall` ≡ rol Admin del legacy).
   */
  rolDisplay = computed(() => {
    const u = this.user();
    if (!u) return '';
    if (this.auth.hasPermission('cliente:viewall')) return 'ADMIN';
    if (this.auth.hasPermission('cliente:view')) return 'OPERADOR';
    return 'CLIENTE';
  });

  chatNoLeidos = computed(() => this.chat.totalNoLeidos());

  estaOnline = signal(true);

  get items() {
    return this._items;
  }

  retryLoadMenu() {
    void this.navigationService.retry();
  }

  irAPerfil() {
    this.router.navigate(['/user-profile']);
  }

  irAChat() {
    this.router.navigate(['/chat']);
  }

  cerrarSesion() {
    this.auth.logOut();
  }

  clearSearch() {
    this.searchValue.set('');
    setTimeout(() => this.menu.instance?.collapseAll(), 0);
  }

  onScroll(e: Event) {
    const el = e.target as HTMLElement;
    this.scrolledTop.set(el.scrollTop > 4);
    this.scrolledBottom.set(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    if (!this.suppressScrollSave) {
      // Persistir scrollTop en sessionStorage para sobrevivir al toggle del
      // drawer (DevExtreme oculta el panel pero re-aplica layout al reabrir,
      // y a veces resetea el scroll). Throttle implicito por evento del browser.
      try { sessionStorage.setItem(this.SCROLL_STORAGE_KEY, String(el.scrollTop)); } catch { /* sessionStorage puede estar bloqueado */ }
    }
  }

  /** Si el item seleccionado esta fuera del viewport, lo scrolleamos a la vista.
   *  Si no hay seleccionado o ya esta visible, restauramos el scrollTop persistido.
   *  Llamado despues de setSelectedItem y despues de cargar items. */
  private restoreScrollPosition() {
    if (!this.scrollEl) return;
    const root = this.elementRef.nativeElement as HTMLElement;
    const selected = root.querySelector('.dx-treeview-item.dx-state-selected, .dx-treeview-node.dx-state-selected > .dx-item .dx-treeview-item') as HTMLElement | null;

    if (selected) {
      const containerRect = this.scrollEl.getBoundingClientRect();
      const itemRect = selected.getBoundingClientRect();
      const isVisible = itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom;
      if (!isVisible) {
        this.suppressScrollSave = true;
        // `block: 'center'` deja el item activo a media altura del sidebar — patron
        // de apps modernas (VSCode explorer, Linear, Notion) cuando hay item activo
        // pero el usuario abrio el panel desde otro contexto.
        selected.scrollIntoView({ block: 'center', behavior: 'auto' });
        // Esperamos un frame para que el browser termine de aplicar el scroll
        // antes de re-habilitar el guardado, evitando pisar el valor "scrolled-into".
        requestAnimationFrame(() => { this.suppressScrollSave = false; });
        return;
      }
    }

    // Sin item activo o ya visible: restaurar el scrollTop persistido.
    try {
      const saved = sessionStorage.getItem(this.SCROLL_STORAGE_KEY);
      const top = saved != null ? Number(saved) : NaN;
      if (Number.isFinite(top) && top > 0) {
        this.suppressScrollSave = true;
        this.scrollEl.scrollTop = top;
        requestAnimationFrame(() => { this.suppressScrollSave = false; });
      }
    } catch { /* ignore */ }
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
      e.preventDefault();
      const root = this.elementRef.nativeElement as HTMLElement;
      const el = root.querySelector('.nav-search-input') as HTMLInputElement | null;
      el?.focus();
      el?.select();
    }
  }

  private rebuildItems(source: AppNavItem[]) {
    this._items = source.map((item) => {
      const path = item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path;
      return { ...item, path, expanded: false };
    });
  }

  setSelectedItem() {
    if (!this.menu.instance) {
      return;
    }
    const url = String(this.selectedItem || '');
    const key = this.findKeyByPath(this._items as any, url);
    if (key) {
      this.menu.instance.selectItem(key);
      // Despues de marcar el item activo, asegurar que sea visible. Doble RAF
      // para que el dx-treeview pinte la clase `dx-state-selected` antes de medir.
      requestAnimationFrame(() => requestAnimationFrame(() => this.restoreScrollPosition()));
    }
  }

  private findKeyByPath(items: any[] | undefined, path: string): string | null {
    if (!items?.length || !path) return null;
    for (const it of items) {
      if (it.path === path) return it.key as string;
      const nested = this.findKeyByPath(it.items, path);
      if (nested) return nested;
    }
    return null;
  }

  /** Recolecta todos los paths del menu (recursivo, incluye hojas y grupos)
   *  para que el `SidebarPinnedService.cleanupAgainst()` descarte links rotos. */
  private collectAllPaths(items: AppNavItem[] | undefined, acc: Set<string> = new Set()): Set<string> {
    if (!items) return acc;
    for (const it of items) {
      if (it.path) acc.add(it.path);
      if (it.items?.length) this.collectAllPaths(it.items, acc);
    }
    return acc;
  }

  /**
   * Togglea pin/unpin sobre un item del menu. Llamado desde el HTML al hover
   * sobre un item (boton "fijar" que aparece al lado del badge).
   * El `$event.stopPropagation()` lo hace el HTML — aca el handler solo cambia
   * el estado del service, sin navegar.
   */
  togglePinned(item: { path?: string; text?: string; icon?: string }): void {
    if (!item?.path) return;
    this.pinned.toggle({
      path: item.path,
      label: item.text ?? item.path,
      icon: item.icon,
    });
  }

  /** Navegar a un item pinned/recent — usado por los botones de esas secciones. */
  navigateToPath(path: string): void {
    if (!path) return;
    this.router.navigateByUrl(path);
  }

  /** Toggle dark/light desde el control inline del sidebar. */
  toggleThemeMode(): void { this.themeService.switchTheme(); }

  /** Toggle normal/compact desde el control inline del sidebar. */
  toggleDensity(): void { this.themeService.toggleDensity(); }

  /** Abrir el Command Palette (⌘K) — atajo en el sidebar para usuarios que no
   *  conocen el shortcut de teclado. */
  openCommandPalette(): void { this.commandPalette.open(); }

  onItemClick(event: DxTreeViewTypes.ItemClickEvent) {
    // Cierro el tooltip — al navegar, el siguiente render el sidebar puede
    // colapsarse y el mouse queda sobre otro punto, dejando el tooltip flotando.
    this.onItemLeave();
    this.selectedItemChanged.emit(event);
  }

  /**
   * Hover sobre un item del menu — solo posiciona el tooltip si el sidebar
   * esta en compact. La X se calcula del borde derecho del rail VISIBLE
   * (`.dx-drawer-panel-content` del layout, que en compact se clamps a
   * `minMenuSize=48`), no del item: los items son full-width del .menu-container
   * (250px+) que se desborda y se clipea — `rect.right` daria una coord
   * fuera del rail. La Y sí del item para alinear vertical con su pill.
   */
  onItemHover(ev: MouseEvent, item: { text?: string; badge?: { text?: string }; items?: AppNavItem[] }) {
    if (!this.compactSig()) return;
    const target = ev.currentTarget as HTMLElement | null;
    if (!target) return;
    const itemRect = target.getBoundingClientRect();

    // Buscamos el panel del drawer (ancestor) para conocer el ancho visible
    // del rail. Si no lo encontramos, fallback al minMenuSize hardcoded (48).
    const railRight = this.getRailRight() ?? 48;

    // Cancelar cualquier cierre debounced pendiente — el usuario sigue en el rail.
    this.clearFlyoutCloseTimer();

    // Si el item es grupo (tiene hijos), abrimos el FLYOUT con todos los hijos
    // en vez del tooltip simple. Caso comun: el usuario en rail quiere ir a un
    // submodulo sin tener que abrir el sidebar entero.
    if (item.items?.length) {
      this.hoveredText.set(null);  // Cerramos el tooltip simple si estaba abierto.
      this.hoveredBadge.set(null);
      this.flyoutItem.set({
        text: item.text ?? '',
        items: item.items,
        // Alinear al tope del item — el flyout crece hacia abajo. El SCSS
        // tiene un `transform: translateY(-8px)` para que el primer item del
        // flyout quede aprox a la altura del item del rail.
        top: Math.round(itemRect.top),
      });
      this.tooltipLeft.set(Math.round(railRight + 8));
      return;
    }

    // Sin hijos: tooltip simple del label (comportamiento original).
    this.flyoutItem.set(null);
    this.tooltipLeft.set(Math.round(railRight + 14));
    this.tooltipTop.set(Math.round(itemRect.top + itemRect.height / 2));
    this.hoveredText.set(item.text ?? '');
    this.hoveredBadge.set(item.badge?.text ?? null);
  }

  /** Devuelve la coordenada X del borde derecho del rail visible (drawer panel). */
  private getRailRight(): number | null {
    const root = this.elementRef.nativeElement as HTMLElement;
    const panel = root.closest('.dx-drawer-panel-content') as HTMLElement | null;
    if (!panel) return null;
    const rect = panel.getBoundingClientRect();
    return rect.right;
  }

  onItemLeave() {
    this.hoveredText.set(null);
    this.hoveredBadge.set(null);
    // Si hay un flyout abierto, no cerramos inmediatamente — damos 150ms para
    // que el mouse pueda viajar del rail al flyout. Si el mouse entra al
    // flyout, su (mouseenter) llama a clearFlyoutCloseTimer() y cancela esto.
    if (this.flyoutItem()) {
      this.scheduleFlyoutClose();
    }
  }

  /** Cancela el cierre debounced del flyout. Llamado al volver a hover en el
   *  rail o al entrar al propio flyout. */
  clearFlyoutCloseTimer(): void {
    if (this.flyoutCloseTimer) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  /** Programa el cierre del flyout en 150ms. Se cancela si el mouse entra al
   *  flyout antes de ese plazo. */
  scheduleFlyoutClose(): void {
    this.clearFlyoutCloseTimer();
    this.flyoutCloseTimer = setTimeout(() => {
      this.flyoutItem.set(null);
      this.flyoutCloseTimer = null;
    }, 150);
  }

  /** Click en un item del flyout — navega y cierra el flyout. */
  onFlyoutItemClick(item: AppNavItem): void {
    if (item.path) this.router.navigateByUrl(item.path);
    this.flyoutItem.set(null);
    this.clearFlyoutCloseTimer();
  }

  /**
   * Hover sobre los quick-btn del fondo (Chat, Configuración, Salir).
   * Reutiliza el rail-tooltip que ya muestran los items del menu — mismo
   * patron visual, fixed position que escapa cualquier overflow.
   *
   * Recibe una translate key (no el string ya resuelto) porque Angular no
   * permite pipes en action expressions de event handlers — resolvemos aca
   * dentro con `translate.instant()`.
   */
  onQuickHover(ev: MouseEvent, labelKey: string, badge: string | null) {
    if (!this.compactSig()) return;
    const target = ev.currentTarget as HTMLElement | null;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const railRight = this.getRailRight() ?? 48;
    this.tooltipLeft.set(Math.round(railRight + 14));
    this.tooltipTop.set(Math.round(rect.top + rect.height / 2));
    this.hoveredText.set(this.translate.instant(labelKey));
    this.hoveredBadge.set(badge);
  }

  ngOnInit() {
    void this.refreshUser();
  }

  private async refreshUser() {
    const res = await this.auth.getUser();
    this.user.set(res.data ?? null);
  }

  ngAfterViewInit() {
    // Cacheamos el contenedor scrolleable una sola vez — es el `.nav-scroll` que
    // tiene el listener (scroll) en el template. Lo usamos para restaurar la
    // posicion del scroll y para scroll-into-view del item seleccionado.
    const root = this.elementRef.nativeElement as HTMLElement;
    this.scrollEl = root.querySelector('.nav-scroll') as HTMLElement | null ?? undefined;

    this.sub = this.navigationService.items$.subscribe((items) => {
      this.rebuildItems(items);
      // Limpiar pinned items que ya no existen en el menu actual (permiso revocado,
      // modulo desactivado, etc.) — evita links rotos.
      this.pinned.cleanupAgainst(this.collectAllPaths(items));
      // Estado inicial: TODO colapsado. Si no estamos en compact y hay un item
      // seleccionado, expandimos solo su grupo padre para que el usuario vea
      // donde esta. En compact NO expandimos: el rail muestra solo iconos L1.
      requestAnimationFrame(() => {
        try {
          this.menu.instance?.collapseAll();
          if (!this._compactMode && this._selectedItem) {
            this.menu.instance?.expandItem(this._selectedItem);
          }
        } catch { /* dx-treeview no listo */ }
        this.setSelectedItem();
        requestAnimationFrame(() => this.restoreScrollPosition());
      });
    });
    this.navigationService.ensureLoaded();

    this.setSelectedItem();
    // ANTES: emitiamos `openMenu` en CUALQUIER dxclick, lo que forzaba a abrir
    // el sidebar antes de que `navigationChanged` pudiera decidir si era un
    // path-item (navegar directo, mantener compact) o un grupo (abrir y
    // expandir). El layout abria el sidebar prematuramente y el flag
    // `wasCompact` quedaba siempre falso. Removido: ahora el sidebar se abre
    // SOLO desde la hamburguesa del header (siempre visible) o desde la
    // logica explicita de `navigationChanged` cuando se clickea un grupo.
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
